import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Clock, CheckCircle2, ArrowRight, Loader2, Edit3 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface Questionnaire {
  id: string;
  name: string;
  description?: string;
  deadline?: string;
  status: string;
  organization?: { name: string };
  _count?: { entries: number };
  fields?: any[];
}

export default function MyQuestionnairesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: questionnaires, isLoading } = useQuery<Questionnaire[]>({
    queryKey: ['questionnaires'],
    queryFn: async () => {
      const res = await api.get('/questionnaires', { params: { limit: 200 } });
      const raw = res.data;
      return Array.isArray(raw) ? raw : (raw?.data ?? []);
    },
  });

  const { data: mySubmissions } = useQuery<any[]>({
    queryKey: ['my-submissions'],
    queryFn: async () => {
      const res = await api.get('/questionnaires/my-submissions');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });

  const correctMutation = useMutation({
    mutationFn: async ({ entryId, values }: { entryId: string; values: { fieldId: string; value: any }[] }) => {
      return api.patch(`/questionnaires/entries/${entryId}/correct`, { values });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-submissions'] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const active = (questionnaires || []).filter(q => q.status === 'ACTIVE');

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Meine Fragebögen</h1>
        <p className="text-sm text-gray-500 mt-1">
          Auszufüllende Fragebögen für {user?.organizationId ? 'deine Organisation' : 'dich'}
        </p>
      </div>

      {active.length === 0 ? (
        <div className="card text-center py-16">
          <ClipboardList size={48} className="mx-auto text-gray-300 mb-3" />
          <h2 className="text-lg font-semibold text-gray-700">Keine offenen Fragebögen</h2>
          <p className="text-sm text-gray-400 mt-1">
            Aktuell sind keine Fragebögen zum Ausfüllen verfügbar.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {active.map((q) => (
            <div
              key={q.id}
              className="card hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/questionnaires/${q.id}/fill`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <ClipboardList size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                      {q.name}
                    </h3>
                    {q.description && (
                      <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{q.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {q.organization?.name && <span>{q.organization.name}</span>}
                      {q._count?.entries != null && (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 size={12} className="text-green-500" />
                          {q._count.entries} Einreichung{q._count.entries !== 1 ? 'en' : ''}
                        </span>
                      )}
                      {q.deadline && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          bis {format(new Date(q.deadline), 'dd.MM.yyyy', { locale: de })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <ArrowRight size={20} className="text-gray-300 group-hover:text-primary transition-colors shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Auch inaktive anzeigen */}
      {(mySubmissions || []).length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm font-medium text-gray-500 mb-3">
            <Edit3 size={14} className="inline mr-1" />Heutige Einreichungen (korrigierbar)
          </h2>
          <div className="space-y-2">
            {(mySubmissions || []).map((sub: any) => (
              <div key={sub.id} className="card bg-amber-50/50 border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{sub.questionnaire?.name || '?'}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(sub.submittedAt).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })} Uhr · {sub.dataEntries?.length || 0} Felder
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/questionnaires/${sub.questionnaire?.id}/fill?edit=${sub.id}`)}
                    className="btn-secondary text-xs py-1 flex items-center gap-1"
                  >
                    <Edit3 size={12} /> Korrigieren
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {questionnaires && questionnaires.filter(q => q.status !== 'ACTIVE').length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm font-medium text-gray-500 mb-3">Abgeschlossen / Archiviert</h2>
          <div className="space-y-2">
            {questionnaires.filter(q => q.status !== 'ACTIVE').map((q) => (
              <div key={q.id} className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-400">
                <ClipboardList size={16} />
                <span className="flex-1">{q.name}</span>
                <span className="badge text-xs">{q.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
